'use client'

import { useState, useEffect, useContext } from 'react'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/shadcn/table'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Loader2, AlertCircle } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import { ManagerContext } from '@/contexts/ManagerContext'
const COSTS = {
  inputTokens: 6, // $6 per million tokens
  outputTokens: 30, // $30 per million tokens
  integrations: 2, // $2 per integration
  minutes: 2, // $2 per 60 minutes
}

const calculateCost = (value, type) => {
  if (!value) return 0
  switch (type) {
    case 'inputTokens':
      return (value / 1000000) * COSTS.inputTokens
    case 'outputTokens':
      return (value / 1000000) * COSTS.outputTokens
    case 'integrations':
      return value * COSTS.integrations
    case 'minutes':
      return (value / 60) * COSTS.minutes
    default:
      return 0
  }
}

const calculateTotalCost = details => {
  if (!details) return 0
  return (
    calculateCost(details.inputTokenLimit || 0, 'inputTokens') +
    calculateCost(details.outputTokenLimit || 0, 'outputTokens') +
    calculateCost(details.integrationLimit || 0, 'integrations') +
    calculateCost(details.minuteLimit || 0, 'minutes')
  )
}

const calculateCurrentUsageCost = (usage, tier) => {
  if (!usage || !tier) return 0

  // Calculate usage beyond tier limits
  const inputTokensOverLimit = Math.max(
    0,
    (usage.totalInputTokens || 0) - tier.inputTokenLimit,
  )
  const outputTokensOverLimit = Math.max(
    0,
    (usage.totalOutputTokens || 0) - tier.outputTokenLimit,
  )
  const integrationsOverLimit = Math.max(
    0,
    (usage.totalIntegrations || 0) - tier.integrationLimit,
  )
  const minutesOverLimit = Math.max(
    0,
    (usage.totalMinutes || 0) - tier.minuteLimit,
  )

  return (
    calculateCost(inputTokensOverLimit, 'inputTokens') +
    calculateCost(outputTokensOverLimit, 'outputTokens') +
    calculateCost(integrationsOverLimit, 'integrations') +
    calculateCost(minutesOverLimit, 'minutes')
  )
}

export default function BillingNextPayment({
  billingDetails,
  tierDetails,
  currentTier,
  usage,
  appliedDiscount = null,
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [upcomingInvoice, setUpcomingInvoice] = useState(null)
  const authContext = useContext(AuthContext)
  const managerContext = useContext(ManagerContext)

  useEffect(() => {
    const fetchUpcomingInvoice = async () => {
      if (!managerContext.billingDetails?.tier) return
      try {
        const { data } = await axios(
          `/organization/${authContext.user.organization.id}/upcoming-invoice`,
          { method: 'GET' },
          authContext,
          true, // Set Content-Type
          'billing', // Use Billing URL
        )
        setUpcomingInvoice(data)
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to fetch upcoming invoice',
        )
      } finally {
        setLoading(false)
      }
    }

    if (billingDetails?.subscriptionId) {
      fetchUpcomingInvoice()
    } else {
      setLoading(false)
    }
  }, [authContext, billingDetails])

  const getCurrentTier = () => {
    return tierDetails?.find(tier => tier.name === currentTier)
  }

  const getCurrentTierPrice = () => {
    const currentTierData = getCurrentTier()
    return (currentTierData?.price || 0) * (billingDetails?.teamSize || 1)
  }

  const calculateDiscountAmount = baseAmount => {
    if (!appliedDiscount) return 0

    if (appliedDiscount.amountOff) {
      // Fixed amount discount (convert from cents to dollars)
      return appliedDiscount.amountOff / 100
    }

    if (appliedDiscount.percentOff) {
      // Percentage discount
      return baseAmount * (appliedDiscount.percentOff / 100)
    }

    return 0
  }

  const getDiscountedPrice = baseAmount => {
    const discountAmount = calculateDiscountAmount(baseAmount)
    return Math.max(0, baseAmount - discountAmount)
  }

  const hasEffectiveDiscount = () => {
    if (!appliedDiscount) return false
    const discountAmount = calculateDiscountAmount(getCurrentTierPrice())
    return discountAmount > 0
  }

  const getNextDueDate = () => {
    if (upcomingInvoice?.period_end) {
      return new Date(upcomingInvoice.period_end * 1000).toLocaleDateString()
    }

    // Fallback: calculate next month from current date if no invoice data
    const nextMonth = new Date()
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    return nextMonth.toLocaleDateString()
  }

  const calculateTotalMonthlyCost = () => {
    const tierPrice = getCurrentTierPrice()
    const discountedTierPrice = getDiscountedPrice(tierPrice)
    const additionalCost = calculateTotalCost(billingDetails)
    return discountedTierPrice + additionalCost
  }

  const calculateCurrentTotalCost = () => {
    const tierPrice = getCurrentTierPrice()
    const discountedTierPrice = getDiscountedPrice(tierPrice)
    const currentTierData = getCurrentTier()
    const currentUsageCost = calculateCurrentUsageCost(usage, currentTierData)
    return discountedTierPrice + currentUsageCost
  }

  if (currentTier === 'Free') {
    return null
  }

  if (loading) {
    return (
      <div>
        <div className="vertically-centered">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </div>
    )
  }

  const currentTierData = getCurrentTier()

  return (
    <Card className="gap-3 py-4">
      <CardContent className="px-4">
        <h6 className="text-base font-semibold mb-2">Next Payment Breakdown</h6>
        {error && (
          <Alert variant="destructive" className="mb-2">
            <AlertCircle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  Base Tier Cost{' '}
                  {billingDetails?.teamSize > 1 &&
                    `(${billingDetails?.teamSize} users)`}
                </TableCell>
                <TableCell className="text-right">
                  ${getCurrentTierPrice().toFixed(2)}/month
                </TableCell>
              </TableRow>
              {hasEffectiveDiscount() && (
                <>
                  <TableRow>
                    <TableCell>
                      <span className="text-green-600">
                        Coupon Discount{' '}
                        {appliedDiscount.code && `(${appliedDiscount.code})`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-green-600">
                        {appliedDiscount.percentOff
                          ? `-${appliedDiscount.percentOff}%`
                          : `-$${calculateDiscountAmount(getCurrentTierPrice()).toFixed(2)}`}
                      </span>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <span className="font-medium">Discounted Tier Cost</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">
                        ${getDiscountedPrice(getCurrentTierPrice()).toFixed(2)}
                        /month
                      </span>
                    </TableCell>
                  </TableRow>
                </>
              )}
              <TableRow>
                <TableCell>Maximum Additional Usage Cost</TableCell>
                <TableCell className="text-right">
                  ${calculateTotalCost(billingDetails).toFixed(2)}/month
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Current Usage Cost (Beyond Tier Limits)</TableCell>
                <TableCell className="text-right">
                  $
                  {calculateCurrentUsageCost(usage, currentTierData).toFixed(2)}
                  /month
                </TableCell>
              </TableRow>
              {upcomingInvoice && upcomingInvoice.amount_due > 0 && (
                <TableRow>
                  <TableCell>Prorated Amount</TableCell>
                  <TableCell className="text-right">
                    ${(upcomingInvoice.amount_due / 100).toFixed(2)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <span className="font-bold">Maximum Next Payment</span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-bold">
                    ${calculateTotalMonthlyCost().toFixed(2)}
                  </span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="font-bold">Current Next Payment</span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-bold">
                    ${calculateCurrentTotalCost().toFixed(2)}
                  </span>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Next Due Date</TableCell>
                <TableCell className="text-right">{getNextDueDate()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
